/**
 * Display Control Utility
 * Provides unified API for display control across Electron and Capacitor platforms
 *
 * Electron: Uses native display sleep commands (macOS, Windows, Linux)
 * Capacitor Android (Rooted): Uses native display control via su commands
 * Capacitor Android (Non-rooted): Uses brightness control as "soft off"
 * Capacitor iOS: Uses brightness control as "soft off"
 */

import { logger } from './logger';

const log = logger.child('displayControl');

// Conditional imports for Capacitor - only import when actually on Capacitor platform
let Capacitor = null;
let SimpleBrightness = null;
let DisplayControlNative = null;
let capacitorVersion = null;

// Flag to track if modules have been loaded
let modulesLoaded = false;

// Lazy load Capacitor modules to avoid webpack warnings
const loadCapacitorModules = async () => {
  if (modulesLoaded) return;

  try {
    // Try to get Capacitor from window first (it's injected by native app)
    if (typeof window !== 'undefined' && window.Capacitor) {
      Capacitor = window.Capacitor;
    } else {
      // Fall back to import
      const capacitorCore = await import('@capacitor/core');
      Capacitor = capacitorCore.Capacitor;
    }

    if (Capacitor && Capacitor.isNativePlatform()) {
      // In Capacitor 7, plugins registered in MainActivity are available via Capacitor.Plugins
      if (Capacitor.Plugins && Capacitor.Plugins.SimpleBrightness) {
        SimpleBrightness = Capacitor.Plugins.SimpleBrightness;
      } else {
        // Fallback: try registerPlugin
        const { registerPlugin } = await import('@capacitor/core');
        SimpleBrightness = registerPlugin('SimpleBrightness');
      }

      try {
        if (Capacitor.Plugins && Capacitor.Plugins.DisplayControl) {
          DisplayControlNative = Capacitor.Plugins.DisplayControl;
        } else {
          const displayControlNative = await import('./displayControlNative');
          DisplayControlNative = displayControlNative.default;
        }
      } catch (e) {
        // DisplayControlNative not available - this is fine
      }
    }

    modulesLoaded = true;
  } catch (e) {
    // Capacitor packages not available (e.g., in Electron or web build)
    // Create stub implementations
    Capacitor = {
      isNativePlatform: () => false,
      getPlatform: () => 'web'
    };
    modulesLoaded = true;
  }
};

// Initialize immediately
loadCapacitorModules();

// Platform detection
const isElectron = () => typeof window !== 'undefined' && window.electronAPI;
const isCapacitor = () => Capacitor && Capacitor.isNativePlatform();
const isAndroid = () => Capacitor && Capacitor.getPlatform() === 'android';

// Store original brightness for restoration
let originalBrightness = null;
let displayState = 'on'; // Track current state
let isDeviceRooted = null; // Cache root status
let isKeepAwakeActive = false; // Track keep awake state

// Event listeners for state changes
const displayStateListeners = new Set();

/**
 * Subscribe to display state changes
 * @param {Function} callback - Called with new state ('on' or 'off')
 * @returns {Function} Unsubscribe function
 */
export const subscribeToDisplayState = (callback) => {
  displayStateListeners.add(callback);
  return () => displayStateListeners.delete(callback);
};

/**
 * Notify listeners of state change
 */
const notifyStateChange = (newState) => {
  displayStateListeners.forEach(callback => {
    try {
      callback(newState);
    } catch (e) {
      log.error('listener threw', e, { newState });
    }
  });
};

/**
 * Check if Android device is rooted
 */
const checkIfRooted = async () => {
  if (isDeviceRooted !== null) {
    return isDeviceRooted; // Return cached value
  }
  
  if (isAndroid() && isCapacitor()) {
    try {
      const result = await DisplayControlNative.isRooted();
      isDeviceRooted = result.rooted || false;
      log.debug('root detection', { rooted: isDeviceRooted });
      return isDeviceRooted;
    } catch (error) {
      log.warn('root check failed', error);
      isDeviceRooted = false;
      return false;
    }
  }
  
  isDeviceRooted = false;
  return false;
};

/**
 * Check if display control is supported on current platform
 */
export const isDisplayControlSupported = async () => {
  await loadCapacitorModules();

  if (isElectron()) {
    try {
      return await window.electronAPI.isDisplayControlSupported();
    } catch (error) {
      return false;
    }
  }

  if (isCapacitor()) {
    // Capacitor brightness control is supported on both iOS and Android
    return true;
  }

  // Web browsers don't support display control
  return false;
};

/**
 * Turn display off (or dim to minimum on mobile)
 */
export const turnDisplayOff = async () => {
  await loadCapacitorModules();
  
  try {
    if (isElectron()) {
      // Electron: Use native display sleep
      const result = await window.electronAPI.turnDisplayOff();
      if (result.success) {
        displayState = 'off';
        notifyStateChange('off');
      }
      return result;
    }
    
    if (isCapacitor()) {
      // Check if Android is rooted
      if (isAndroid()) {
        const rooted = await checkIfRooted();
        
        if (rooted) {
          // Rooted Android: Use native display control
          try {
            const result = await DisplayControlNative.turnDisplayOff();
            if (result.success) {
              displayState = 'off';
              notifyStateChange('off');
              return { success: true, state: 'off', method: 'rooted', rooted: true };
            }
            // If rooted method fails, fall back to brightness
            log.warn('rooted display off failed, falling back to brightness', { command: 'turnDisplayOff' });
          } catch (error) {
            log.warn('rooted display off threw', error, { command: 'turnDisplayOff', platform: 'android' });
          }
        }
      }
      
      // Non-rooted or iOS: Store current brightness and dim to minimum
      if (!SimpleBrightness) {
        return { success: false, error: 'Screen brightness control not available' };
      }

      try {
        // Use the new SimpleBrightness plugin
        const result = await SimpleBrightness.turnScreenOff();
        displayState = 'off';
        notifyStateChange('off');
        originalBrightness = result.savedBrightness;
        return { success: true, state: 'off', method: 'brightness', rooted: false };
      } catch (error) {
        log.warn('brightness turnScreenOff failed', error, { command: 'turnDisplayOff', method: 'brightness' });
        return { success: false, error: error.message, method: 'brightness' };
      }
    }

    return { success: false, error: 'Platform not supported' };
  } catch (error) {
    log.error('turnDisplayOff threw', error);
    return { success: false, error: error.message };
  }
};

/**
 * Turn display on (or restore brightness on mobile)
 */
export const turnDisplayOn = async () => {
  await loadCapacitorModules();
  
  try {
    if (isElectron()) {
      // Electron: Use native display wake
      const result = await window.electronAPI.turnDisplayOn();
      if (result.success) {
        displayState = 'on';
        notifyStateChange('on');
      }
      return result;
    }
    
    if (isCapacitor()) {
      // Check if Android is rooted
      if (isAndroid()) {
        const rooted = await checkIfRooted();
        
        if (rooted) {
          // Rooted Android: Use native display control
          try {
            const result = await DisplayControlNative.turnDisplayOn();
            if (result.success) {
              displayState = 'on';
              notifyStateChange('on');
              return { success: true, state: 'on', method: 'rooted', rooted: true };
            }
            // If rooted method fails, fall back to brightness
            log.warn('rooted display on failed, falling back to brightness', { command: 'turnDisplayOn' });
          } catch (error) {
            log.warn('rooted display on threw', error, { command: 'turnDisplayOn', platform: 'android' });
          }
        }
      }
      
      // Non-rooted or iOS: Restore original brightness or set to 50% default
      if (!SimpleBrightness) {
        return { success: false, error: 'Screen brightness control not available' };
      }

      try {
        // Use the new SimpleBrightness plugin
        const result = await SimpleBrightness.turnScreenOn();
        displayState = 'on';
        notifyStateChange('on');
        originalBrightness = null; // Clear stored brightness
        return { success: true, state: 'on', method: 'brightness', rooted: false };
      } catch (error) {
        log.warn('brightness turnScreenOn failed', error, { command: 'turnDisplayOn', method: 'brightness' });
        return { success: false, error: error.message, method: 'brightness' };
      }
    }

    return { success: false, error: 'Platform not supported' };
  } catch (error) {
    log.error('turnDisplayOn threw', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get current display schedule
 */
export const getDisplaySchedule = async () => {
  await loadCapacitorModules();
  
  if (isElectron()) {
    try {
      return await window.electronAPI.getDisplaySchedule();
    } catch (error) {
      log.warn('getDisplaySchedule (electron) failed', error);
      return null;
    }
  }

  if (isCapacitor()) {
    // Load from localStorage for Capacitor
    try {
      const scheduleJson = localStorage.getItem('display-schedule');
      return scheduleJson ? JSON.parse(scheduleJson) : null;
    } catch (error) {
      log.warn('getDisplaySchedule (localStorage) failed', error, { key: 'display-schedule' });
      return null;
    }
  }
  
  return null;
};

/**
 * Set display schedule
 */
export const setDisplaySchedule = async (schedule) => {
  await loadCapacitorModules();
  
  if (isElectron()) {
    try {
      return await window.electronAPI.setDisplaySchedule(schedule);
    } catch (error) {
      log.warn('setDisplaySchedule (electron) failed', error);
      return { success: false, error: error.message };
    }
  }
  
  if (isCapacitor()) {
    // Save to localStorage for Capacitor
    try {
      if (schedule && schedule.enabled) {
        localStorage.setItem('display-schedule', JSON.stringify(schedule));
        // Start schedule monitoring
        startScheduleMonitoring(schedule);
      } else {
        localStorage.removeItem('display-schedule');
        stopScheduleMonitoring();
      }
      return { success: true, message: 'Schedule saved' };
    } catch (error) {
      log.warn('setDisplaySchedule (localStorage) failed', error, { key: 'display-schedule' });
      return { success: false, error: error.message };
    }
  }
  
  return { success: false, error: 'Platform not supported' };
};

/**
 * Turn screen completely off (uses native display control, requires root on Android)
 */
export const turnScreenOff = async () => {
  await loadCapacitorModules();

  try {
    if (isElectron()) {
      return await window.electronAPI.turnDisplayOff();
    }

    if (isCapacitor() && DisplayControlNative) {
      const result = await DisplayControlNative.turnDisplayOff();
      if (result.success) {
        displayState = 'off';
        notifyStateChange('off');
        return { success: true, state: 'off', method: result.method };
      }
      // If native control failed (not rooted), return error
      return { success: false, error: 'Screen off requires root access. Use brightness mode instead.', method: result.method };
    }

    return { success: false, error: 'Platform not supported' };
  } catch (error) {
    log.error('turnScreenOff threw', error);
    return { success: false, error: error.message };
  }
};

/**
 * Turn screen back on (uses native display control)
 */
export const turnScreenOn = async () => {
  await loadCapacitorModules();

  try {
    if (isElectron()) {
      return await window.electronAPI.turnDisplayOn();
    }

    if (isCapacitor() && DisplayControlNative) {
      const result = await DisplayControlNative.turnDisplayOn();
      if (result.success) {
        displayState = 'on';
        notifyStateChange('on');
        return { success: true, state: 'on', method: result.method };
      }
      // If native control failed, try waking with SimpleBrightness
      if (SimpleBrightness) {
        try {
          await SimpleBrightness.turnScreenOn();
          displayState = 'on';
          notifyStateChange('on');
          return { success: true, state: 'on', method: 'brightness-fallback' };
        } catch (e) {
          log.warn('brightness fallback failed', e, { command: 'turnScreenOn' });
        }
      }
      return { success: false, error: 'Failed to turn screen on', method: result.method };
    }

    return { success: false, error: 'Platform not supported' };
  } catch (error) {
    log.error('turnScreenOn threw', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get current display state
 */
export const getDisplayState = () => displayState;

/**
 * Get platform information
 */
export const getPlatformInfo = async () => {
  await loadCapacitorModules();
  
  if (isElectron()) {
    return {
      platform: 'electron',
      method: 'native',
      capabilities: ['schedule', 'hard-off'],
      rooted: false
    };
  }
  
  if (isCapacitor()) {
    const platform = Capacitor.getPlatform();
    
    // Check if Android is rooted
    if (isAndroid()) {
      const rooted = await checkIfRooted();
      return {
        platform: platform,
        method: rooted ? 'rooted' : 'brightness',
        capabilities: rooted ? ['schedule', 'hard-off'] : ['schedule', 'soft-off'],
        rooted: rooted
      };
    }
    
    // iOS always uses brightness
    return {
      platform: platform,
      method: 'brightness',
      capabilities: ['schedule', 'soft-off'],
      rooted: false
    };
  }
  
  return {
    platform: 'web',
    method: 'none',
    capabilities: [],
    rooted: false
  };
};

// Schedule monitoring (for Capacitor)
let scheduleMonitorInterval = null;

/**
 * Check if current time is within scheduled off time
 */
const isWithinScheduledOffTime = (schedule) => {
  if (!schedule || !schedule.enabled) {
    return false;
  }

  const now = new Date();
  const currentHour = now.getHours();
  const currentMinute = now.getMinutes();
  const currentTimeInMinutes = currentHour * 60 + currentMinute;

  const startHour = parseInt(schedule.startHour);
  const startMinute = parseInt(schedule.startMinute || 0);
  const endHour = parseInt(schedule.endHour);
  const endMinute = parseInt(schedule.endMinute || 0);

  const startTimeInMinutes = startHour * 60 + startMinute;
  const endTimeInMinutes = endHour * 60 + endMinute;

  // Handle overnight schedules (e.g., 22:00 to 06:00)
  if (startTimeInMinutes > endTimeInMinutes) {
    return currentTimeInMinutes >= startTimeInMinutes || currentTimeInMinutes < endTimeInMinutes;
  } else {
    return currentTimeInMinutes >= startTimeInMinutes && currentTimeInMinutes < endTimeInMinutes;
  }
};

/**
 * Start monitoring schedule (Capacitor only)
 */
const startScheduleMonitoring = (schedule) => {
  // Clear existing interval
  stopScheduleMonitoring();
  
  // Check immediately
  checkAndApplySchedule(schedule);
  
  // Check every minute
  scheduleMonitorInterval = setInterval(() => {
    checkAndApplySchedule(schedule);
  }, 60000);

  log.debug('schedule monitoring started');
};

/**
 * Stop monitoring schedule
 */
const stopScheduleMonitoring = () => {
  if (scheduleMonitorInterval) {
    clearInterval(scheduleMonitorInterval);
    scheduleMonitorInterval = null;
    log.debug('schedule monitoring stopped');
  }
};

/**
 * Check and apply schedule
 */
const checkAndApplySchedule = async (schedule) => {
  if (!schedule || !schedule.enabled) {
    return;
  }

  const shouldBeOff = isWithinScheduledOffTime(schedule);

  log.debug('schedule check', { shouldBeOff, currentState: displayState });

  if (shouldBeOff && displayState !== 'off') {
    log.debug('turning display off per schedule');
    await turnDisplayOff();
  } else if (!shouldBeOff && displayState === 'off') {
    log.debug('turning display on per schedule');
    await turnDisplayOn();
  }
};

/**
 * Enable keep awake (prevent screen from sleeping)
 */
export const enableKeepAwake = async () => {
  await loadCapacitorModules();

  if (isCapacitor() && SimpleBrightness) {
    try {
      await SimpleBrightness.keepAwake();
      isKeepAwakeActive = true;
      log.debug('keep awake enabled');
      return { success: true, message: 'Keep awake enabled' };
    } catch (error) {
      log.warn('enableKeepAwake failed', error);
      return { success: false, error: error.message };
    }
  }
  return { success: false, error: 'Platform not supported' };
};

/**
 * Disable keep awake (allow screen to sleep normally)
 */
export const disableKeepAwake = async () => {
  await loadCapacitorModules();

  if (isCapacitor() && SimpleBrightness) {
    try {
      await SimpleBrightness.allowSleep();
      isKeepAwakeActive = false;
      log.debug('keep awake disabled');
      return { success: true, message: 'Keep awake disabled' };
    } catch (error) {
      log.warn('disableKeepAwake failed', error);
      return { success: false, error: error.message };
    }
  }
  return { success: false, error: 'Platform not supported' };
};

/**
 * Check if keep awake is currently active
 */
export const isKeepAwakeEnabled = () => isKeepAwakeActive;

/**
 * Initialize display control (call on app startup for Capacitor)
 */
export const initializeDisplayControl = async () => {
  await loadCapacitorModules();
  
  if (isCapacitor()) {
    // Enable keep awake by default to keep screen on while app is active
    await enableKeepAwake();
    
    // Load and apply saved schedule
    const schedule = await getDisplaySchedule();
    if (schedule && schedule.enabled) {
      startScheduleMonitoring(schedule);
    }
  }
  // Electron handles this in its own process
};

/**
 * Cleanup display control (call on app shutdown)
 */
export const cleanupDisplayControl = async () => {
  await loadCapacitorModules();
  
  stopScheduleMonitoring();
  
  // Disable keep awake when app is closing
  if (isCapacitor()) {
    await disableKeepAwake();
  }
};

